import React from 'react';
import { Plus, ListTodo, CheckCircle2, Circle, Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { cn } from '../../utils/cn';
import type { TodoTask } from '../../types';

interface GroupTasksListProps {
    tasks: TodoTask[];
    newTaskTitle: string;
    onNewTaskTitleChange: (title: string) => void;
    onAddTask: (e: React.SyntheticEvent<HTMLFormElement>) => void;
    onToggleTask: (taskId: string, completed: boolean) => void;
    onDeleteTask: (taskId: string) => void;
}

export const GroupTasksList: React.FC<GroupTasksListProps> = ({
    tasks,
    newTaskTitle,
    onNewTaskTitleChange,
    onAddTask,
    onToggleTask,
    onDeleteTask
}) => {
    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-text-heading">Group Tasks</h3>
            </div>

            <form onSubmit={onAddTask} className="flex gap-2">
                <Input
                    placeholder="Add a task..."
                    value={newTaskTitle}
                    onChange={(e) => { onNewTaskTitleChange(e.target.value); }}
                    className="h-10"
                />
                <Button type="submit" size="icon">
                    <Plus className="h-5 w-5" />
                </Button>
            </form>

            {tasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="mb-4 rounded-full bg-bg-card-alt p-4">
                        <ListTodo className="h-8 w-8 text-text-faint" />
                    </div>
                    <p className="text-sm text-text-muted">No shared tasks yet.</p>
                </div>
            ) : (
                <div className="rounded-2xl border border-border-default bg-bg-card p-2 shadow-sm space-y-1">
                    {tasks.map((task) => (
                        <div key={task.id} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-bg-base group">
                            <button
                                onClick={() => { onToggleTask(task.id, !task.completed); }}
                                className="flex items-center gap-3 text-left"
                            >
                                {task.completed ? (
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                ) : (
                                    <Circle className="h-5 w-5 text-text-faint" />
                                )}
                                <span className={cn(
                                    "text-sm font-medium transition-all",
                                    task.completed ? "text-text-faint line-through" : "text-text-body"
                                )}>
                                    {task.title}
                                </span>
                            </button>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100"
                                onClick={() => { onDeleteTask(task.id); }}
                            >
                                <Trash2 className="h-4 w-4 text-red-400" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
